/**
 * Run `fn` every `everyMs` while the page is visible, and at once whenever it
 * comes back. Returns a disposer.
 *
 * Coming back is the case that bites. A phone that opened the app, locked, and
 * was picked up three hours later does not reload the page: iOS resumes the
 * same document with every `setInterval` frozen where it was, and a desktop
 * tab in the background has its timers throttled to once a minute at best. So
 * nothing that polls on a timer alone is current when somebody looks, which is
 * the only moment it matters. Three signals mean "look again now":
 *
 * - `visibilitychange` to visible — the tab or the app is in front again;
 * - `pageshow` with `persisted` — restored from the back-forward cache, which
 *   skips every boot path the app has;
 * - `online` — the connection is back, and whatever failed while it was gone
 *   is worth asking for once more. `announceReconnected()` is the same signal
 *   for the case the browser cannot see: the network never went away, our
 *   server did, and `state/connection.svelte.ts` is what noticed it return.
 *
 * `fn` may run twice for one return (two of those fire together on some
 * engines), so it must be cheap when nothing is stale: every caller here gates
 * on the age of what it holds rather than trusting this to call it once.
 *
 * Nothing runs at start. Every caller has just loaded what it would refresh.
 */
/** Every live `whileVisible` caller's catch-up, for `announceReconnected`. */
const reconnectors = new Set<() => void>()

/**
 * Tell every live refresher the server is answering again. The browser fires
 * `online` only when the *device* regains a network; a server that was
 * unreachable over a working network comes back with no event at all, and
 * everything would otherwise wait out its own interval to find out.
 */
export function announceReconnected(): void {
  for (const fn of [...reconnectors]) fn()
}

export function whileVisible(fn: () => void, everyMs: number): () => void {
  let timer: ReturnType<typeof setInterval> | null = null

  const stop = () => {
    if (timer !== null) clearInterval(timer)
    timer = null
  }
  const start = () => {
    stop()
    // Refused rather than started and immediately cleared: the app can mount
    // into a background tab.
    if (document.hidden) return
    timer = setInterval(fn, everyMs)
  }
  const onVisibility = () => {
    if (document.hidden) {
      stop()
      return
    }
    fn()
    start()
  }
  const onPageShow = (e: PageTransitionEvent) => {
    if (e.persisted) onVisibility()
  }
  const onOnline = () => {
    if (!document.hidden) fn()
  }

  start()
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pageshow', onPageShow)
  window.addEventListener('online', onOnline)
  reconnectors.add(onOnline)

  return () => {
    stop()
    reconnectors.delete(onOnline)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pageshow', onPageShow)
    window.removeEventListener('online', onOnline)
  }
}
